/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { forwardRef, useState } from 'react';
import type { ForwardRefRenderFunction } from 'react';

import type { LearnerProfile_UserLocation as UserLocation } from '__generated__/graphql-types-do-not-use';

import { SelectOption, Typography2 } from '@coursera/cds-core';

import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';
import { getAlgoliaRestrictedCountryFilters } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/countryUtils';
import {
  getLocationItem,
  getLocationLabel,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileUtils';
import type { ComboBoxItem } from 'bundles/employability-common/components/ComboBox';
import ComboBox from 'bundles/employability-common/components/ComboBox';
import { searchMatcher } from 'bundles/employability-common/components/utils';
import { useCitySearchQuery } from 'bundles/employability-common/queries/hooks/useCitySearchQuery';

import _t from 'i18n!nls/account-profile';

type Props = {
  value?: UserLocation | null;
  onChange: (location: UserLocation) => void;
  validationStatus?: 'success' | 'error';
  validationLabel?: string;
  'data-e2e'?: string;
};

const RESTRICTED_COUNTRIES_EXCLUSION_FILTERS = getAlgoliaRestrictedCountryFilters();

const styles = {
  label: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  selectOption: css`
    width: 100%;
  `,
  optionalText: css`
    color: var(--cds-color-grey-600);
    font-style: italic;
  `,
};

const LocationInput: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { value, onChange, validationStatus, validationLabel, 'data-e2e': e2e },
  ref
) => {
  const [query, setQuery] = useState<string>('');

  const { cities, citiesError } = useCitySearchQuery({
    search: query,
    limit: 30,
    countriesExclusive: RESTRICTED_COUNTRIES_EXCLUSION_FILTERS,
  });
  const errorMessage = citiesError ? _t('Error loading locations, please try again.') : undefined;

  const cityLabels = cities.map((location) =>
    getLocationLabel({
      cityName: location.city,
      countryName: location.country,
    })
  );
  const showState = cityLabels.length !== new Set(cityLabels).size;

  const comboBoxItems: ComboBoxItem[] = cities.map((location) => {
    const { lat: latitude, lng: longitude } = location;
    const key = `${latitude}-${longitude}`;
    const label = getLocationLabel({
      cityName: location.city,
      countryName: location.country,
      stateName: showState ? location.adminName : undefined,
    });
    const item = getLocationItem(location, label);

    return {
      key,
      label,
      value: key,
      onClick: (closePopover?: () => void) => {
        onChange({
          cityName: location.city,
          countryName: location.country,
          latitude,
          longitude,
          countryCode: location.iso2,
        });
        closePopover?.();
      },
      match: (term) => searchMatcher(item, term),
      content: () => (
        <SelectOption css={styles.selectOption} value={key} selected={key === `${value?.latitude}-${value?.longitude}`}>
          {label}
        </SelectOption>
      ),
    };
  });

  return (
    <div ref={ref} data-e2e={e2e}>
      <Typography2 css={styles.label} component="label" variant="subtitleMedium">
        <RequiredFieldLabel labelComponent={<span>{_t('Location')}</span>} />
      </Typography2>
      <ComboBox
        items={comboBoxItems}
        onSearch={(q) => setQuery(q || '')}
        inactivePlaceholder={value ? getLocationLabel(value) : _t('Select your city')}
        activePlaceholder={_t('Select location')}
        fullWidth={true}
        errorMessage={validationStatus === 'error' && validationLabel ? validationLabel : errorMessage}
      />
    </div>
  );
};

export default forwardRef(LocationInput);
